import { Typography } from '@mui/material';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
  ApiRequest_AssignPhoneNumber_PropertyLocation,
  ApiResponse_AssignPhoneNumber_PropertyLocation,
  useAssignPhoneNumber,
} from '@/api/endpoints/propertyLocation/useAssignPhoneNumber';
import { PropertyLocationModel } from '@/api/models/propertyLocation';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { formatPhoneNumber } from '@/utils/phone';

import ModalWithForm from '../modals/ModalWithForm';

interface Props {
  phoneNumber: string;
  propertyLocationId: PropertyLocationModel['id'];
  onClose: () => void;
  onPhoneNumberSelected?: (
    propertyLocation: ApiResponse_AssignPhoneNumber_PropertyLocation
  ) => void;
}

const PropertyLocationPhoneNumberAssignModal: React.FC<Props> = ({
  phoneNumber,
  propertyLocationId,
  onClose,
  onPhoneNumberSelected,
}) => {
  const { setOnboardingProgress } = useOnboardingProgress();

  const form = useForm<ApiRequest_AssignPhoneNumber_PropertyLocation>({
    defaultValues: {
      phoneNumber,
    },
  });

  const assignPhoneNumberMutation = useAssignPhoneNumber(propertyLocationId, {
    onSuccess: data => {
      setOnboardingProgress({
        hasPhoneNumber: true,
      });
      onPhoneNumberSelected?.(data);
      onClose();
    },
  });

  const onSubmit = useCallback(
    (data: ApiRequest_AssignPhoneNumber_PropertyLocation) =>
      assignPhoneNumberMutation.mutateAsync({
        phoneNumber: data.phoneNumber,
      }),
    [assignPhoneNumberMutation]
  );

  return (
    <ModalWithForm
      onClose={onClose}
      title="Select Phone Number"
      form={form}
      maxWidth="sm"
      onSubmit={onSubmit}
    >
      <Typography variant="body2">
        This number will become the main contact for your property — tenants, vendors, and others
        will use it to reach you. Don’t worry, all calls to this number will be forwarded straight
        to your phone. Terr.ai handles everything behind the scenes, and you’ll always see the
        number on your dashboard when you need it.
      </Typography>

      <Typography variant="body2">
        Selected phone number: <strong>{formatPhoneNumber(phoneNumber)}</strong>.
      </Typography>

      <Typography variant="body2">
        <strong>If you need to change the number in the future, you can contact support.</strong>
      </Typography>
    </ModalWithForm>
  );
};

export default PropertyLocationPhoneNumberAssignModal;
